import { useState, useEffect } from 'react';
import { List, ListItem, ListItemText, Typography, Divider, CircularProgress, Box, Button, Chip } from '@mui/material';
import axios from 'axios';
import EventNoteIcon from '@mui/icons-material/EventNote'; // 🎯 Icon for Court Reminders

export default function NotificationsPage() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userId, setUserId] = useState(null);

  useEffect(() => {
    let isMounted = true;
    axios
      .get('https://demo.insollex.com/api/user', { withCredentials: true })
      .then((res) => {
        if (isMounted) {
          setUserId(res.data.id);
          setError('');
        }
      })
      .catch(() => {
        if (isMounted) {
          setError('User not logged in');
          setLoading(false);
        }
      });
    return () => { isMounted = false; };
  }, []);

  useEffect(() => {
    if (!userId) return;
    const fetchNotifications = async () => {
      try {
        const response = await axios.get('https://demo.insollex.com/api/notifications', {
          params: { userId },
        });

        if (response.data && response.data.notifications) {
          setNotifications(response.data.notifications);
        } else {
          setNotifications([]);
        }
      } catch (error) {
        console.error('Error fetching notifications:', error);
        setError('Failed to fetch notifications');
      } finally {
        setLoading(false);
      }
    };
    fetchNotifications();
  }, [userId]);

  const handleMarkAllRead = async () => {
  try {
    // 🎯 FIX: Move userId from the body {} to the params: {} block
    await axios.put('https://demo.insollex.com/api/notifications/markAllAsRead', null, { 
      params: { userId } 
    });
    
    // Update local state so the UI reflects the change immediately
    setNotifications(prev => prev.map(n => ({ ...n, isUnRead: false }))); 
  } catch (error) {
    console.error('Error marking notifications as read:', error);
  }
};

  if (loading) {
    return (
      <Box sx={{ padding: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ padding: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">Notifications & Reminders</Typography>
        <Button 
          variant="contained" 
          onClick={handleMarkAllRead} 
          disabled={!notifications.some(n => n.isUnRead)}
        >
          Mark all as read
        </Button>
      </Box>
      <Divider sx={{ mb: 2 }} />

      {error ? (
        <Typography variant="body1" color="error" sx={{ mb: 2 }}>{error}</Typography>
      ) : notifications.length === 0 ? (
        <Typography variant="body1">No notifications available.</Typography>
      ) : (
        <List>
          {notifications.map((notification) => {
            // 🎯 Determine if this is a Court Order Reminder based on content
            const isReminder = notification.title?.includes('Court Order') || notification.type === 'Reminder' || notification.title?.includes('Due') || notification.title?.includes('Reminder') || notification.title?.includes('Renewal');

            return (
              <ListItem 
                key={notification.id}
                sx={{ 
                  mb: 1, 
                  borderRadius: 1,
                  backgroundColor: notification.isUnRead ? '#f0f7ff' : 'transparent',
                  borderLeft: notification.isUnRead ? '4px solid #1976d2' : '4px solid #e0e0e0',
                  transition: '0.2s'
                }}
              >
                <Box sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>
                  {isReminder ? <EventNoteIcon color="primary" /> : null}
                </Box>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: notification.isUnRead ? 'bold' : 'normal' }}>
                        {notification.title}
                      </Typography>
                      {isReminder && (
                        <Chip label="Next Business Day Reminder" size="small" color="info" variant="outlined" />
                      )}
                    </Box>
                  }
                  secondary={
                    <>
                      <Typography variant="body2" color="text.primary" sx={{ mt: 0.5 }}>
                        {notification.description}
                      </Typography>
                      {notification.createdAt && (
                        <Typography variant="caption" color="text.secondary">
                          Generated: {new Date(notification.createdAt).toLocaleString()}
                        </Typography>
                      )}
                    </>
                  }
                />
              </ListItem>
            );
          })}
        </List>
      )}
    </Box>
  );
}